import React, { useState, useEffect } from "react";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { services as mockServices } from "../mock";
import { useLang } from "../i18n/LanguageContext";
import SectionBg from "./SectionBg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback list built from mock (used only until API responds)
const fallback = mockServices.map((s, i) => ({
  id: s.id, image: s.image, link: s.id === "pizzeria" || s.id === "fastfood" ? "https://deliveroo.it/it/menu/oristano/oristano/langolo" : "#prenota",
  _mockId: s.id, order: i,
}));

export default function Services() {
  const { t, lang } = useLang();
  const [services, setServices] = useState(fallback);
  const [fromApi, setFromApi] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) {
          setServices(res.data);
          setFromApi(true);
        }
      })
      .catch(() => {});
  }, []);

  const titleOf = (s) => fromApi ? (lang === "en" && s.title_en ? s.title_en : s.title) : t(`services.items.${s._mockId}.title`);
  const descOf = (s) => fromApi ? (lang === "en" && s.desc_en ? s.desc_en : s.desc) : t(`services.items.${s._mockId}.desc`);
  const ctaOf = (s) => fromApi ? (lang === "en" && s.cta_en ? s.cta_en : (s.cta || "Scopri")) : t(`services.items.${s._mockId}.cta`);

  // responsive grid: keep 4 columns max, wraps nicely for more items
  return (
    <section id="servizi" className="py-24 md:py-32 bg-[#1C1815] relative grain overflow-hidden">
      <SectionBg section="services" lightText />
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16 reveal">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-12 bg-[#C9A24B]" />
            <span className="text-[#C9A24B] tracking-[0.3em] text-xs uppercase">{t("services.badge")}</span>
            <span className="h-px w-12 bg-[#C9A24B]" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-white leading-tight">
            {t("services.title1")} <span className="italic text-[#C9A24B]">{t("services.highlight")}</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, idx) => {
            const href = s.link || "#prenota";
            return (
              <a key={s.id} href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-[2px] reveal block"
                style={{ transitionDelay: `${(idx % 4) * 90}ms` }}>
                <div className="h-[420px] overflow-hidden">
                  <img src={s.image} alt={titleOf(s)} className="zoom-img w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f0c0a] via-[#0f0c0a]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-display text-2xl text-white mb-2">{titleOf(s)}</h3>
                  <p className="text-white/70 text-sm leading-relaxed mb-4 line-clamp-3">{descOf(s)}</p>
                  <span className="inline-flex items-center gap-1.5 text-[#C9A24B] text-sm font-medium tracking-wide">
                    {ctaOf(s)}
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
